import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../../../../api';
import moment from 'moment';
import { 
  buildFacultyMap, 
  resolveFacultyName, 
  aggregateFacultyForSelection,
  fetchFacultyNameById 
} from '../../hooks/useTimetable';

export default function WeeklyTimetableView({ 
  tenantId, 
  classId, 
  academicYearId, 
  semesterType,
  termId,
  termName,
  isSemesterBased,
  classFacultyAssignments = [],
  facultyData = [],
  subjects = [],
  token,
  electiveGroups = [],
  onOverrideClick,
  labBatches = []
}) {
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(moment().startOf('isoWeek'));
  const [weekInfo, setWeekInfo] = useState(null);
  const [error, setError] = useState(null);
  const [additionalFaculty, setAdditionalFaculty] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [overrideData, setOverrideData] = useState({});
  const [showOverride, setShowOverride] = useState(true);
  const [loadingOverrides, setLoadingOverrides] = useState({});
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const selectedWeekStr = useMemo(() => selectedWeek.format('YYYY-MM-DD'), [selectedWeek]);
  const lastFetchKeyRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchedFacultyRef = useRef(new Set());
  const processingRef = useRef(false);
  const lastDataHashRef = useRef(null);

  // Build faculty map from all sources
  const facultyMap = useMemo(() => {
    console.log('[WeeklyView] Building faculty map');
    return buildFacultyMap(
      [...facultyData, ...additionalFaculty], 
      subjects, 
      classFacultyAssignments
    );
  }, [facultyData, additionalFaculty, subjects, classFacultyAssignments]);

  const resolveFacultyLabel = useCallback((value) => {
    return resolveFacultyName(value, facultyMap) || '';
  }, [facultyMap]);

  // Helper to enrich elective selections with subgroups from group definition
  const enrichElectiveSelectionsWithSubgroups = useCallback((entry) => {
    if (!entry.electiveGroupId || !entry.electiveSelections || entry.electiveSelections.length === 0) {
      return entry;
    }

    const group = electiveGroups.find(g => String(g._id) === String(entry.electiveGroupId));
    if (!group || !group.subjects) {
      return entry;
    }

    const enrichedSelections = entry.electiveSelections.map(selection => {
      const groupSubject = group.subjects.find(s => 
        String(s.subjectId) === String(selection.subjectId)
      );

      if (groupSubject?.subGroups && groupSubject.subGroups.length > 0 && 
          (!selection.subgroupSelections || selection.subgroupSelections.length === 0)) {
        
        const subgroupSelections = groupSubject.subGroups.map(subgroup => ({
          subGroupId: subgroup.subGroupId,
          facultyId: subgroup.facultyId,
          room: subgroup.room || selection.room || entry.room,
          facultyName: ''
        }));

        return {
          ...selection,
          subgroupSelections: subgroupSelections
        };
      }
      
      return selection;
    });

    return {
      ...entry,
      electiveSelections: enrichedSelections
    };
  }, [electiveGroups]);

  // Helper to get session type
  const getSessionType = useCallback((entry) => {
    if (entry.type === 'Lab' || (entry.subjectId && entry.batchId) || entry.isLab) {
      return 'lab';
    }
    if (entry.type === 'Break') {
      return 'break';
    }
    if (entry.type === 'Custom') {
      return 'custom';
    }
    if (entry.electiveGroupId) {
      const electiveGroup = electiveGroups.find(g => String(g._id) === String(entry.electiveGroupId));
      const groupType = electiveGroup?.groupType || electiveGroup?.groupCategory || electiveGroup?.category || '';
      if (groupType === 'language' || groupType === 'Language') {
        return 'language';
      }
      return 'elective';
    }
    return 'regular';
  }, [electiveGroups]);

const getBatchDisplayName = useCallback((batchId) => {
  if (!batchId) return '';
  
  // Extract the actual ID string from various possible formats
  let batchIdStr = '';
  if (typeof batchId === 'string') {
    batchIdStr = batchId;
  } else if (batchId._id) {
    batchIdStr = String(batchId._id);
  } else if (batchId.$oid) {
    batchIdStr = batchId.$oid;
  } else if (batchId.toString) {
    batchIdStr = batchId.toString();
  } else {
    batchIdStr = String(batchId);
  }
  
  // Find the batch in labBatches array
  const batch = labBatches.find(b => {
    const bId = String(b._id || b.id || b.batchId);
    return bId === batchIdStr;
  });
  
  // Return the batch name with proper formatting
  if (batch) {
    // Use batchPrefix + batchNumber if available
    if (batch.batchPrefix && batch.batchNumber) {
      return `${batch.batchPrefix} ${batch.batchNumber}`;
    }
    if (batch.name) return batch.name;
    if (batch.batchName) return batch.batchName;
    if (batch.batchNumber) return `Batch ${batch.batchNumber}`;
    return 'Lab Batch';
  }
  
  // If batch not found, try to extract from the batchId object
  if (batchId.name) return batchId.name;
  if (batchId.batchName) return batchId.batchName;
  if (batchId.batchNumber) {
    if (batchId.batchPrefix) return `${batchId.batchPrefix} ${batchId.batchNumber}`;
    return `Batch ${batchId.batchNumber}`;
  }
  
  return '';
}, [labBatches]);

  const getGroupDisplayName = useCallback((groupId) => {
    const group = electiveGroups.find(g => String(g._id) === String(groupId));
    if (!group) return 'Group';
    
    const groupType = group.groupType || group.groupCategory || group.category || '';
    const isLanguage = groupType === 'language' || groupType === 'Language';
    
    if (isLanguage) {
      return group.groupName || group.name || 'Language';
    }
    return `Elective: ${group.groupName || group.name || 'Elective'}`;
  }, [electiveGroups]);

  const getElectiveGroupLabel = useCallback((groupId) => {
    const g = electiveGroups.find(x => String(x._id) === String(groupId));
    return g?.label || '';
  }, [electiveGroups]);

  const getElectiveGroupName = useCallback((groupId) => {
    const g = electiveGroups.find(x => String(x._id) === String(groupId));
    return g?.groupName || 'Elective Group';
  }, [electiveGroups]);

  const getSubjectDisplayName = useCallback((subjectId) => {
    if (!subjectId) return 'Subject';
    const s = subjects.find(x => String(x._id || x.id) === String(subjectId));
    return s?.name || s?.subjectName || 'Subject';
  }, [subjects]);

  const getSubjectCode = useCallback((subjectId) => {
    if (!subjectId) return '';
    const s = subjects.find(x => String(x._id || x.id) === String(subjectId));
    return s?.subjectCode || s?.code || '';
  }, [subjects]);

  const weekOptions = useMemo(() => {
    const options = [];
    const currentWeek = moment().startOf('isoWeek');
    
    for (let i = 4; i > 0; i--) {
      const week = currentWeek.clone().subtract(i, 'weeks');
      options.push({
        value: week.format('YYYY-MM-DD'),
        label: `${week.format('MMM DD')} - ${week.clone().add(6, 'days').format('MMM DD, YYYY')}`,
        isCurrent: false
      });
    }
    
    options.push({
      value: currentWeek.format('YYYY-MM-DD'),
      label: `Current Week: ${currentWeek.format('MMM DD')} - ${currentWeek.clone().add(6, 'days').format('MMM DD, YYYY')}`,
      isCurrent: true
    });
    
    for (let i = 1; i <= 4; i++) {
      const week = currentWeek.clone().add(i, 'weeks');
      options.push({
        value: week.format('YYYY-MM-DD'),
        label: `${week.format('MMM DD')} - ${week.clone().add(6, 'days').format('MMM DD, YYYY')}`,
        isCurrent: false
      });
    }
    
    return options;
  }, []);

  const weekDays = useMemo(() => {
    const start = moment(selectedWeekStr).startOf('isoWeek');
    return Array.from({ length: 7 }).map((_, i) => start.clone().add(i, 'days'));
  }, [selectedWeekStr]);

  const shouldSkipClasses = useCallback((day) => {
    if (!day.skip) return false;
    
    const eventType = day.skipEvent?.eventType?.toLowerCase() || '';
    const eventName = day.skipEvent?.eventName?.toLowerCase() || '';
    
    const skipTypes = ['exam', 'holiday', 'public holiday', 'university holiday'];
    const skipKeywords = ['exam', 'examination', 'holiday', 'break', 'closed'];
    
    return skipTypes.includes(eventType) || 
           skipKeywords.some(keyword => eventName.includes(keyword)) ||
           skipKeywords.some(keyword => eventType.includes(keyword));
  }, []);

  const fetchOverridesForWeek = useCallback(async () => {
    if (!tenantId || !classId || !academicYearId || !isMountedRef.current) return;

    try {
      const startDate = weekDays[0].format('YYYY-MM-DD');
      const endDate = weekDays[6].format('YYYY-MM-DD');

      const params = {
        tenantId,
        classId,
        academicYearId,
        startDate,
        endDate
      };

      if (!isSemesterBased && termId) {
        params.termId = termId;
      } else if (isSemesterBased && semesterType) {
        params.semesterType = semesterType;
      }

      console.log('📅 Fetching overrides with params:', params);

      const response = await api.get('/api/timetable/daily-override/range', { params });
      
      if (response.data.success && isMountedRef.current) {
        const overrideMap = {};
        response.data.data.forEach(override => {
          overrideMap[override.date] = {
            hasOverride: true,
            entryCount: override.entryCount,
            hasModifications: override.hasModifications,
            hasCancellations: override.hasCancellations,
            description: override.description,
            reason: override.reason
          };
        });
        setOverrides(overrideMap);
      }
    } catch (error) {
      console.error('Error fetching overrides for week:', error);
    }
  }, [tenantId, classId, academicYearId, termId, semesterType, isSemesterBased, weekDays]);

  const fetchOverrideForDate = useCallback(async (dateISO) => {
    if (!tenantId || !classId || !academicYearId || !dateISO || !isMountedRef.current) return null;

    setLoadingOverrides(prev => ({ ...prev, [dateISO]: true }));

    try {
      const params = {
        tenantId,
        classId,
        academicYearId,
        targetDate: dateISO
      };

      if (!isSemesterBased && termId) {
        params.termId = termId;
      } else if (isSemesterBased && semesterType) {
        params.semesterType = semesterType;
      }

      const response = await api.get('/api/timetable/daily-override/get', { params });
      
      if (response.data.success && response.data.data && isMountedRef.current) {
        const override = response.data.data;
        
        const enrichedEntries = await Promise.all((override.overriddenEntries || []).map(async entry => {
          const enrichedEntry = enrichElectiveSelectionsWithSubgroups(entry);
          const facultyVal = enrichedEntry.facultyUserId || enrichedEntry.facultyId || enrichedEntry.faculty || enrichedEntry.facultyName;
          let resolvedName = resolveFacultyLabel(facultyVal);

          const sessionType = getSessionType(enrichedEntry);
          let labFacultyName = '';
          // For lab sessions with batch, fetch faculty from backend and resolve name
          if (sessionType === 'lab' && enrichedEntry.batchId && enrichedEntry.subjectId) {
            let batchId = enrichedEntry.batchId;
            if (typeof batchId === 'object' && batchId !== null) {
              batchId = batchId._id || batchId.id || batchId.batchId || (batchId.toString ? batchId.toString() : String(batchId));
            }
            let subjectId = enrichedEntry.subjectId;
            if (typeof subjectId === 'object' && subjectId !== null) {
              subjectId = subjectId._id || subjectId.id || subjectId.subjectId || (subjectId.toString ? subjectId.toString() : String(subjectId));
            }
            try {
              const res = await api.get(`/api/classManager/lab-batches/${batchId}/subjects/${subjectId}/faculty`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (res.data.faculty && res.data.faculty.length > 0) {
                const names = await Promise.all(res.data.faculty.map(f => fetchFacultyNameById(f.facultyId, token)));
                labFacultyName = names.filter(Boolean).join(', ');
              } else {
                labFacultyName = 'No Faculty Assigned';
              }
            } catch (err) {
              labFacultyName = 'Error fetching faculty';
            }
          }

          const enrichedElectives = (enrichedEntry.electiveSelections || []).map(selection => ({
            ...selection,
            facultyName: aggregateFacultyForSelection(selection, facultyMap) || selection.facultyName || '',
            subgroupSelections: (selection.subgroupSelections || []).map(sg => ({
              ...sg,
              facultyName: resolveFacultyLabel(sg.facultyId) || ''
            }))
          }));

          const isElective = sessionType === 'elective' || sessionType === 'language';
          const isLab = sessionType === 'lab';
          const isBreak = sessionType === 'break';
          const isCustom = sessionType === 'custom';

          // Resolve batch name
          let batchName = '';
          if (enrichedEntry.batchId) {
            batchName = getBatchDisplayName(enrichedEntry.batchId);
          }

          return {
            ...enrichedEntry,
            facultyName: labFacultyName || resolvedName || enrichedEntry.facultyName || '',
            electiveSelections: enrichedElectives,
            electiveGroupName: enrichedEntry.electiveGroupId ? getElectiveGroupName(enrichedEntry.electiveGroupId) : null,
            electiveGroupLabel: enrichedEntry.electiveGroupId ? getElectiveGroupLabel(enrichedEntry.electiveGroupId) : null,
            electiveGroupDisplay: enrichedEntry.electiveGroupId ? getGroupDisplayName(enrichedEntry.electiveGroupId) : null,
            isElective,
            isLab,
            isBreak,
            isCustom,
            sessionType,
            batchName: batchName || enrichedEntry.batchName
          };
        }));

        return {
          entries: enrichedEntries,
          description: override.description,
          reason: override.reason,
          hasOverride: true
        };
      }
      return null;
    } catch (error) {
      console.error(`Error fetching override for ${dateISO}:`, error);
      return null;
    } finally {
      if (isMountedRef.current) {
        setLoadingOverrides(prev => ({ ...prev, [dateISO]: false }));
      }
    }
  }, [tenantId, classId, academicYearId, termId, semesterType, isSemesterBased, resolveFacultyLabel, facultyMap, getElectiveGroupName, getElectiveGroupLabel, getSessionType, getBatchDisplayName, getGroupDisplayName, enrichElectiveSelectionsWithSubgroups, token]);

  useEffect(() => {
    const fetchAllOverrides = async () => {
      const overridePromises = [];
      const overrideDates = [];

      weekDays.forEach(day => {
        const dateISO = day.format('YYYY-MM-DD');
        if (overrides[dateISO]?.hasOverride && !overrideData[dateISO]) {
          overridePromises.push(fetchOverrideForDate(dateISO));
          overrideDates.push(dateISO);
        }
      });

      if (overridePromises.length > 0 && isMountedRef.current) {
        const results = await Promise.all(overridePromises);
        const newOverrideData = {};
        overrideDates.forEach((date, index) => {
          if (results[index]) {
            newOverrideData[date] = results[index];
          }
        });
        setOverrideData(prev => ({ ...prev, ...newOverrideData }));
      }
    };

    fetchAllOverrides();
  }, [weekDays, overrides, fetchOverrideForDate, overrideData]);

  const processMissingFaculty = useCallback(async (data) => {
    if (processingRef.current) return;
    if (!data || !isMountedRef.current) return;

    const dataHash = JSON.stringify({
      entries: data.map(day => day.entries?.map(e => ({
        facultyUserId: e.facultyUserId,
        facultyId: e.facultyId,
        electiveSelections: e.electiveSelections?.map(s => ({
          facultyId: s.facultyId,
          subgroupSelections: s.subgroupSelections?.map(sg => sg.facultyId)
        }))
      })))
    });

    if (lastDataHashRef.current === dataHash) return;
    
    processingRef.current = true;
    
    try {
      const facultyIds = new Set(classId);
      
      data.forEach(day => {
        (day.entries || []).forEach(entry => {
          if (entry.facultyUserId) facultyIds.add(String(entry.facultyUserId));
          if (entry.facultyId) facultyIds.add(String(entry.facultyId));
          
          (entry.electiveSelections || []).forEach(selection => {
            if (selection.facultyId) facultyIds.add(String(selection.facultyId));
            
            (selection.subgroupSelections || []).forEach(subgroup => {
              if (subgroup.facultyId) facultyIds.add(String(subgroup.facultyId));
            });
          });
        });
      });

      const missingIds = Array.from(facultyIds).filter(id => 
        !facultyMap.has(id) && id.trim() && !fetchedFacultyRef.current.has(id)
      );

      if (missingIds.length === 0) {
        lastDataHashRef.current = dataHash;
        return;
      }

      console.log('[WeeklyView] Fetching missing faculty:', missingIds);

      const facultyPromises = missingIds.map(async (facultyId) => {
        try {
          const name = await fetchFacultyNameById(facultyId, token);
          fetchedFacultyRef.current.add(facultyId);
          return { id: facultyId, name };
        } catch {
          fetchedFacultyRef.current.add(facultyId);
          return null;
        }
      });
      
      const facultyResults = await Promise.all(facultyPromises);
      const validResults = facultyResults.filter(Boolean);
      
      if (validResults.length > 0 && isMountedRef.current) {
        setAdditionalFaculty(prev => {
          const newFaculty = validResults.filter(r => 
            !prev.some(p => p.userId === r.id)
          );
          return [...prev, ...newFaculty.map(r => ({ 
            userId: r.id, 
            firstName: r.name,
            name: r.name 
          }))];
        });
      }
      
      lastDataHashRef.current = dataHash;
    } finally {
      processingRef.current = false;
    }
  }, [facultyMap,classId, token]);

  const fetchWeeklyTimetable = useCallback(async () => {
    if (!tenantId || !classId || !academicYearId || !isMountedRef.current) {
      const emptyWeekData = weekDays.map(day => ({
        date: day.toDate(),
        dateISO: day.format('YYYY-MM-DD'),
        dayName: day.format('dddd'),
        entries: [],
        events: [],
        skip: false,
        skipEvent: null,
        hasOverride: false
      }));
      if (isMountedRef.current) {
        setWeeklyData(emptyWeekData);
        setError('Missing required parameters for timetable fetch');
        setLoading(false);
      }
      return;
    }

    try {
      const fetchKey = JSON.stringify({ 
        tenantId, 
        classId, 
        academicYearId, 
        semesterType, 
        termId,
        weekStartDate: selectedWeekStr 
      });

      if (lastFetchKeyRef.current === fetchKey) return;
      lastFetchKeyRef.current = fetchKey;

      if (fetchAbortRef.current) {
        try { fetchAbortRef.current.abort(); } catch (e) {}
        fetchAbortRef.current = null;
      }

      const abortController = new AbortController();
      fetchAbortRef.current = abortController;
      setLoading(true);
      setError(null);

      const params = {
        tenantId,
        classId,
        academicYearId,
        weekStartDate: selectedWeekStr
      };

      if (isSemesterBased && semesterType) {
        params.semesterType = semesterType;
      } else if (!isSemesterBased && termId) {
        params.termId = termId;
      }

      console.log('📅 Fetching weekly timetable with params:', params);

      const response = await api.get('/api/timetable/weekly', { 
        params, 
        signal: abortController.signal 
      });

      if (response.data && response.data.success && isMountedRef.current) {
        const serverWeekly = Array.isArray(response.data.data) ? response.data.data : [];
        // Enrich lab batch entries with resolved faculty names
        const enrichedWeekly = await Promise.all(serverWeekly.map(async day => {
          const dateISO = day.dateISO || moment(day.date).format('YYYY-MM-DD');
          const hasOverride = overrides[dateISO]?.hasOverride || false;
          const enrichedEntries = await Promise.all((Array.isArray(day.entries) ? day.entries : []).map(async entry => {
            // Enrich with subgroups first
            const enrichedEntry = enrichElectiveSelectionsWithSubgroups(entry);
            const facultyVal = enrichedEntry.facultyUserId || enrichedEntry.facultyId || enrichedEntry.faculty || enrichedEntry.facultyName;
            let resolvedName = resolveFacultyLabel(facultyVal);
            const sessionType = getSessionType(enrichedEntry);
            let labFacultyName = '';
            // For lab sessions with batch, fetch faculty from backend and resolve name
            if (sessionType === 'lab' && enrichedEntry.batchId && enrichedEntry.subjectId) {
              // Ensure batchId and subjectId are always strings
              let batchId = enrichedEntry.batchId;
              if (typeof batchId === 'object' && batchId !== null) {
                batchId = batchId._id || batchId.id || batchId.batchId || (batchId.toString ? batchId.toString() : String(batchId));
              }
              let subjectId = enrichedEntry.subjectId;
              if (typeof subjectId === 'object' && subjectId !== null) {
                subjectId = subjectId._id || subjectId.id || subjectId.subjectId || (subjectId.toString ? subjectId.toString() : String(subjectId));
              }
              try {
                const res = await api.get(`/api/classManager/lab-batches/${batchId}/subjects/${subjectId}/faculty`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.faculty && res.data.faculty.length > 0) {
                  const names = await Promise.all(res.data.faculty.map(f => fetchFacultyNameById(f.facultyId, token)));
                  labFacultyName = names.filter(Boolean).join(', ');
                } else {
                  labFacultyName = 'No Faculty Assigned';
                }
              } catch (err) {
                labFacultyName = 'Error fetching faculty';
              }
            }
            const enrichedElectives = (enrichedEntry.electiveSelections || []).map(selection => ({
              ...selection,
              facultyName: aggregateFacultyForSelection(selection, facultyMap) || selection.facultyName || '',
              subgroupSelections: (selection.subgroupSelections || []).map(sg => ({
                ...sg,
                facultyName: resolveFacultyLabel(sg.facultyId) || ''
              }))
            }));
            const isElective = sessionType === 'elective' || sessionType === 'language';
            const isLanguage = sessionType === 'language';
            const isLab = sessionType === 'lab';
            const isBreak = sessionType === 'break';
            const isCustom = sessionType === 'custom';
            // Resolve batch name
            let batchName = '';
            if (enrichedEntry.batchId) {
              batchName = getBatchDisplayName(enrichedEntry.batchId);
            }
            return {
              ...enrichedEntry,
              facultyName: labFacultyName || resolvedName || enrichedEntry.facultyName || '',
              electiveSelections: enrichedElectives,
              isElective,
              isLanguage,
              isLab,
              isBreak,
              isCustom,
              sessionType,
              electiveGroupName: enrichedEntry.electiveGroupId ? getElectiveGroupName(enrichedEntry.electiveGroupId) : null,
              electiveGroupLabel: enrichedEntry.electiveGroupId ? getElectiveGroupLabel(enrichedEntry.electiveGroupId) : null,
              electiveGroupDisplay: enrichedEntry.electiveGroupId ? getGroupDisplayName(enrichedEntry.electiveGroupId) : null,
              subjectName: enrichedEntry.subjectName || getSubjectDisplayName(enrichedEntry.subjectId),
              subjectCode: enrichedEntry.subjectCode || getSubjectCode(enrichedEntry.subjectId),
              batchName: batchName || enrichedEntry.batchName
            };
          }));
          return {
            ...day,
            entries: enrichedEntries,
            hasOverride,
            overrideInfo: overrides[dateISO] || null
          };
        }));

        setWeeklyData(enrichedWeekly);
        setWeekInfo(response.data.weekInfo || null);

        processMissingFaculty(enrichedWeekly);
      } else if (isMountedRef.current) {
        setWeeklyData([]);
        setError(response.data?.message || 'Failed to load timetable');
      }
    } catch (error) {
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching weekly timetable:', error);
      if (isMountedRef.current) {
        setError(error.response?.data?.message || error.message || 'Failed to load timetable');
        const emptyWeekData = weekDays.map(day => ({
          date: day.toDate(),
          dateISO: day.format('YYYY-MM-DD'),
          dayName: day.format('dddd'),
          entries: [],
          events: [],
          skip: false,
          skipEvent: null,
          hasOverride: overrides[day.format('YYYY-MM-DD')]?.hasOverride || false,
          overrideInfo: overrides[day.format('YYYY-MM-DD')] || null
        }));
        setWeeklyData(emptyWeekData);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [
    tenantId, classId, academicYearId, semesterType, termId, isSemesterBased, selectedWeekStr, overrides,
    resolveFacultyLabel, facultyMap, processMissingFaculty, getElectiveGroupName, getElectiveGroupLabel,
    getSubjectDisplayName, getSubjectCode, getBatchDisplayName, getGroupDisplayName, getSessionType, weekDays,
    enrichElectiveSelectionsWithSubgroups, token
  ]);

  useEffect(() => {
    let isActive = true;
    if (isActive) {
      fetchOverridesForWeek();
    }
    return () => {
      isActive = false;
    };
  }, [fetchOverridesForWeek, selectedWeekStr]);

  useEffect(() => {
    let isActive = true;
    if (isActive) {
      fetchWeeklyTimetable();
    }
    return () => {
      isActive = false;
    };
  }, [fetchWeeklyTimetable]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (fetchAbortRef.current) {
        try { fetchAbortRef.current.abort(); } catch (e) {}
      }
    };
  }, []);

  const handleWeekChange = useCallback((weekStartDate) => {
    setSelectedWeek(moment(weekStartDate).startOf('isoWeek'));
    setOverrideData({});
    setShowOverride(true);
    fetchedFacultyRef.current.clear();
    lastDataHashRef.current = null;
    processingRef.current = false;
  }, []);

  const navigateWeek = useCallback((direction) => {
    setSelectedWeek(prev => prev.clone().add(direction, 'weeks'));
    setOverrideData({});
    setShowOverride(true);
    fetchedFacultyRef.current.clear();
    lastDataHashRef.current = null;
    processingRef.current = false;
  }, []);

  const handleDayOverride = useCallback((date) => {
    if (onOverrideClick) {
      onOverrideClick(date);
    }
  }, [onOverrideClick]);

  const toggleOverrideView = useCallback(() => {
    setShowOverride(prev => !prev);
  }, []);

  const handleEntryClick = useCallback((entry, dayDate) => {
    setSelectedEntry(entry);
    setSelectedDay(dayDate);
  }, []);

  const closeModal = useCallback(() => {
    setSelectedEntry(null);
    setSelectedDay(null);
  }, []);

  const getDayDisplayData = useCallback((day, dateISO) => {
    const hasOverride = overrides[dateISO]?.hasOverride;
    const overrideEntries = overrideData[dateISO]?.entries || [];
    const normalEntries = day.entries || [];
    
    if (showOverride && hasOverride && overrideEntries.length > 0) {
      return {
        ...day,
        entries: overrideEntries,
        isOverride: true,
        overrideInfo: overrideData[dateISO]
      };
    }
    
    return {
      ...day,
      entries: normalEntries,
      isOverride: false,
      overrideInfo: null
    };
  }, [showOverride, overrides, overrideData]);

  const formatTime = useCallback((timeString) => {
    return moment(timeString, 'HH:mm').format('h:mm A');
  }, []);

  const allTimeSlots = useMemo(() => {
    const slots = new Set();
    weeklyData.forEach(day => {
      const displayDay = getDayDisplayData(day, day.dateISO);
      displayDay.entries.forEach(entry => {
        if (entry.timeSlot) {
          const slotKey = `${entry.timeSlot.start}-${entry.timeSlot.end}`;
          slots.add(slotKey);
        }
      });
    });
    
    return Array.from(slots)
      .map(slot => {
        const [start, end] = slot.split('-');
        return { start, end, key: slot };
      })
      .sort((a, b) => moment(a.start, 'HH:mm').diff(moment(b.start, 'HH:mm')));
  }, [weeklyData, getDayDisplayData]);

  const overrideStats = useMemo(() => {
    const totalDays = weekDays.length;
    const daysWithOverride = Object.keys(overrides).filter(date => overrides[date]?.hasOverride).length;
    return {
      totalDays,
      daysWithOverride,
      showingOverride: showOverride
    };
  }, [weekDays, overrides, showOverride]);

  const renderEntryModal = useCallback(() => {
    if (!selectedEntry) return null;

    const sessionType = selectedEntry.sessionType || getSessionType(selectedEntry);
    const isElective = sessionType === 'elective' || sessionType === 'language';
    const isLanguage = sessionType === 'language';
    const isBreak = sessionType === 'break';
    const isCustom = sessionType === 'custom';
    const isLab = sessionType === 'lab';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-2xl p-6 w-[500px] max-h-[90vh] overflow-y-auto border-t-4 border-blue-600">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-bold text-gray-800">
              {isBreak ? 'Break Session' : isCustom ? 'Custom Session' : isLab ? 'Lab Session' : isLanguage ? 'Language Session' : isElective ? 'Elective Session' : 'Session Details'}
              {selectedDay && <span className="text-sm font-normal text-gray-500 ml-2">({moment(selectedDay).format('MMM DD')})</span>}
            </h3>
            <button onClick={closeModal} className="text-gray-400 hover:text-red-500 font-bold text-xl">✕</button>
          </div>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded text-sm border border-gray-200 grid grid-cols-2 gap-4">
              <div>
                <span className="text-[10px] text-gray-500 font-bold uppercase block">Time</span> 
                <span className="font-semibold text-gray-800">
                  {formatTime(selectedEntry.timeSlot?.start)} - {formatTime(selectedEntry.timeSlot?.end)}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-gray-500 font-bold uppercase block">Room</span> 
                <span className="font-semibold text-gray-800">{selectedEntry.room || '—'}</span>
              </div>
            </div>

            {(isBreak || isCustom) && (
              <div className="p-4 border rounded bg-amber-50 border-amber-200 text-center">
                <div className="text-[10px] text-amber-600 font-bold uppercase mb-1">Session Type</div>
                <div className="font-bold text-gray-800 text-lg">{selectedEntry.label || (isBreak ? 'BREAK' : 'CUSTOM SESSION')}</div>
              </div>
            )}

            {isLab && (
              <div className="p-4 border rounded bg-purple-50 border-purple-200 text-center">
                <div className="text-[10px] text-purple-600 font-bold uppercase mb-1">Lab Session</div>
                <div className="font-bold text-gray-800 text-lg">{selectedEntry.subjectName || getSubjectDisplayName(selectedEntry.subjectId)}</div>
                {selectedEntry.subjectCode && <div className="text-sm text-gray-500 mt-1">{selectedEntry.subjectCode}</div>}
                <div className="text-sm text-gray-600 mt-2">
                  Batch: {selectedEntry.batchName || getBatchDisplayName(selectedEntry.batchId) || 'Not Specified'}
                </div>
                <div className="text-sm text-gray-600 mt-2 font-medium">
                  👨‍🏫 {selectedEntry.facultyName || resolveFacultyLabel(selectedEntry.facultyUserId) || 'No Faculty'}
                </div>
              </div>
            )}

            {isElective && (
              <div>
                <h4 className="font-bold text-sm text-gray-700 mb-2 border-b pb-1 flex items-center gap-2">
                  <span>{isLanguage ? 'Language Allocations' : 'Elective Allocations'}</span>
                  {selectedEntry.electiveGroupLabel && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                      Label: {selectedEntry.electiveGroupLabel}
                    </span>
                  )}
                </h4>
                <div className="mb-2 text-xs text-blue-700 font-semibold">
                  Group: {selectedEntry.electiveGroupDisplay || getGroupDisplayName(selectedEntry.electiveGroupId)}
                </div>
                <div className="max-h-60 overflow-y-auto space-y-3 pr-1">
                  {(selectedEntry.electiveSelections || []).map((sel, i) => (
                    <div key={i} className="p-3 border rounded bg-white text-sm shadow-sm">
                      <div className="font-bold text-purple-800 flex items-center justify-between">
                        <span>{sel.subjectName || getSubjectDisplayName(sel.subjectId)}</span>
                        {sel.subjectCode && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{sel.subjectCode}</span>
                        )}
                      </div>
                      
                      {sel.subgroupSelections?.length > 0 ? (
                        <div className="mt-2 space-y-2">
                          {sel.subgroupSelections.map((sg, sgIdx) => (
                            <div key={sg.subGroupId} className="pl-2 border-l-2 border-purple-200 ml-1">
                              <div className="text-xs text-gray-500">Group {sgIdx + 1}</div>
                              <div className="text-sm font-medium text-gray-700">
                                👨‍🏫 {sg.facultyName || resolveFacultyLabel(sg.facultyId) || 'Not Assigned'}
                              </div>
                              {sg.room && <div className="text-xs text-gray-500">Room: {sg.room}</div>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-sm text-gray-600">👨‍🏫</span>
                          <span className="text-sm font-medium text-gray-700">
                            {sel.facultyName || resolveFacultyLabel(sel.facultyId) || 'Not Assigned'}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isElective && !isBreak && !isCustom && !isLab && (
              <div className="p-4 border rounded bg-blue-50 border-blue-200 text-center">
                <div className="text-[10px] text-blue-600 font-bold uppercase mb-1">Subject</div>
                <div className="font-bold text-gray-800 text-lg">{selectedEntry.subjectName || getSubjectDisplayName(selectedEntry.subjectId)}</div>
                {(selectedEntry.subjectCode || getSubjectCode(selectedEntry.subjectId)) && (
                  <div className="text-xs text-gray-500 mt-1">{selectedEntry.subjectCode || getSubjectCode(selectedEntry.subjectId)}</div>
                )}
                <div className="text-sm text-gray-600 mt-3 font-medium flex items-center justify-center gap-2">
                  <span>👨‍🏫</span>
                  <span>{selectedEntry.facultyName || resolveFacultyLabel(selectedEntry.facultyUserId) || 'No Faculty'}</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-6 flex justify-end">
            <button onClick={closeModal} className="px-4 py-2 bg-gray-800 text-white rounded text-sm hover:bg-gray-900">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }, [selectedEntry, selectedDay, formatTime, resolveFacultyLabel, getSubjectDisplayName, getBatchDisplayName, getGroupDisplayName, getSessionType, closeModal, getSubjectCode]);

  if (loading && !weeklyData.length) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-lg font-medium text-gray-700">Loading Academic Schedule</div>
          <div className="text-sm text-gray-500 mt-2">Retrieving weekly timetable data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="weekly-timetable bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header with Term Info and Override Toggle */}
      <div className="bg-white p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">Weekly Academic Schedule</h1>
              {termName && !isSemesterBased && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                  {termName}
                </span>
              )}
              {isSemesterBased && semesterType && (
                <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                  Semester
                </span>
              )}
            </div>
            {weekInfo && (
              <p className="text-gray-600 font-medium">
                {moment(weekInfo.startDate).format('MMMM DD')} - {moment(weekInfo.endDate).format('MMMM DD, YYYY')}
              </p>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-3">
            {overrideStats.daysWithOverride > 0 && (
              <button
                onClick={toggleOverrideView}
                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  showOverride 
                    ? 'bg-orange-500 text-white hover:bg-orange-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {showOverride ? 'Showing Overrides' : 'Showing Normal Schedule'}
              </button>
            )}

            <div className="flex items-center bg-gray-50 rounded-lg p-1 border border-gray-200">
              <button
                onClick={() => navigateWeek(-1)}
                className="p-2 hover:bg-white rounded-md transition-colors duration-200 disabled:opacity-50"
                disabled={loading}
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <select
                value={selectedWeek.format('YYYY-MM-DD')}
                onChange={(e) => handleWeekChange(e.target.value)}
                className="bg-transparent border-none text-gray-900 font-medium focus:outline-none focus:ring-0 cursor-pointer px-3 py-1"
                disabled={loading}
              >
                {weekOptions.map(option => (
                  <option key={option.value} value={option.value} className="text-gray-900">
                    {option.label}
                  </option>
                ))}
              </select>
              
              <button
                onClick={() => navigateWeek(1)}
                className="p-2 hover:bg-white rounded-md transition-colors duration-200 disabled:opacity-50"
                disabled={loading}
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {overrideStats.daysWithOverride > 0 && (
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-orange-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">
                  {overrideStats.daysWithOverride} day(s) this week have overrides
                </span>
              </div>
              <div className="text-sm text-orange-600">
                {showOverride 
                  ? 'Currently showing override schedules' 
                  : 'Currently showing normal schedules'}
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="m-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-5 h-5 text-red-500">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-red-800 font-medium">Unable to Load Schedule</h3>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
            <button
              onClick={fetchWeeklyTimetable}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Week Days Header */}
      <div className="grid grid-cols-7 gap-4 px-6 py-4 bg-gray-50 border-b border-gray-200">
        {weekDays.map((day, index) => {
          const dateISO = day.format('YYYY-MM-DD');
          const hasOverride = overrides[dateISO]?.hasOverride;
          const isToday = day.isSame(moment(), 'day');
          const isLoading = loadingOverrides[dateISO];
          
          return (
            <div key={dateISO} className="text-center relative">
              <div className={`font-semibold ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                {day.format('ddd')}
              </div>
              <div className={`text-sm ${isToday ? 'text-blue-600' : 'text-gray-600'}`}>
                {day.format('DD MMM')}
              </div>
              {hasOverride && (
                <div className="mt-1">
                  {isLoading ? (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                      <div className="animate-spin h-3 w-3 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleDayOverride(dateISO)}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full hover:bg-orange-200 transition-colors"
                      title="This day has an override. Click to view/modify."
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Override</span>
                    </button>
                  )}
                </div>
              )}
              {isToday && (
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></div>
              )}
            </div>
          );
        })}
      </div>

      {/* Academic Timetable View */}
      <div className="p-6">
        {weeklyData.length === 0 && !loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Schedule Available</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              There are no scheduled classes for the selected week.
            </p>
            <button
              onClick={fetchWeeklyTimetable}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              Refresh Schedule
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border-b border-r border-gray-200 p-4 font-semibold text-gray-700 text-left min-w-[140px]">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">Day / Time</span>
                    </div>
                  </th>
                  {allTimeSlots.map((slot, index) => (
                    <th key={slot.key} className="border-b border-r border-gray-200 p-4 last:border-r-0">
                      <div className="text-center">
                        <div className="font-semibold text-gray-900 text-sm">{formatTime(slot.start)}</div>
                        <div className="text-xs text-gray-500 mt-1">to</div>
                        <div className="font-semibold text-gray-900 text-sm">{formatTime(slot.end)}</div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeklyData.map((day, dayIndex) => {
                  const dateISO = day.dateISO || moment(day.date).format('YYYY-MM-DD');
                  const hasOverride = overrides[dateISO]?.hasOverride;
                  const displayDay = getDayDisplayData(day, dateISO);
                  const isOverrideActive = displayDay.isOverride;
                  
                  return (
                    <tr key={dateISO || dayIndex} className={`hover:bg-gray-50 transition-colors ${
                      isOverrideActive ? 'bg-orange-50' : hasOverride && !showOverride ? 'bg-yellow-50' : ''
                    }`}>
                      <td className="border-b border-r border-gray-200 p-4 bg-gray-50 relative">
                        <div className="text-center">
                          <div className={`font-semibold text-lg ${
                            moment(dateISO).isSame(moment(), 'day') 
                              ? 'text-blue-600' 
                              : 'text-gray-900'
                          }`}>
                            {day.dayName}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {moment(dateISO).format('DD MMM')}
                          </div>
                          
                          {hasOverride && (
                            <div className="mt-2">
                              {isOverrideActive ? (
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-orange-500 text-white text-xs rounded-md">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>Override Active</span>
                                </div>
                              ) : (
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500 text-white text-xs rounded-md">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  <span>Override Available</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {moment(dateISO).isSame(moment(), 'day') && (
                            <div className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full mt-2">
                              Today
                            </div>
                          )}
                          {(day.skip || day.events?.length > 0) && (
                            <div className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full mt-2">
                              {day.skip ? 'Event Day' : `${day.events?.length} Event(s)`}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      {allTimeSlots.map((slot, slotIndex) => {
                        const slotKey = slot.key;
                        const entriesForThisSlot = displayDay.entries.filter(entry => 
                          entry.timeSlot && 
                          `${entry.timeSlot.start}-${entry.timeSlot.end}` === slotKey
                        );
                        
                        const shouldSkip = shouldSkipClasses(day);
                        
                        return (
                          <td key={slotKey} className={`border-b border-r border-gray-200 p-3 align-top last:border-r-0 ${
                            isOverrideActive ? 'bg-orange-50' : hasOverride && !showOverride ? 'bg-yellow-50' : ''
                          }`}>
                            {shouldSkip ? (
                              <div className="text-center py-6">
                                <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-2">
                                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                </div>
                                <div className="text-red-700 font-medium text-sm">
                                  {day.skipEvent?.eventName || 'Academic Event'}
                                </div>
                                {day.skipEvent?.eventType && (
                                  <div className="text-xs text-red-600 mt-1">
                                    {day.skipEvent.eventType}
                                  </div>
                                )}
                              </div>
                            ) : entriesForThisSlot.length > 0 ? (
                              <div className="space-y-2">
                                {entriesForThisSlot.map((entry, entryIndex) => {
                                  const entrySessionType = entry.sessionType || getSessionType(entry);
                                  const isElective = entrySessionType === 'elective' || entrySessionType === 'language';
                                  const isLanguage = entrySessionType === 'language';
                                  const isBreak = entrySessionType === 'break';
                                  const isCustom = entrySessionType === 'custom';
                                  const isLab = entrySessionType === 'lab';
                                  
                                  let bgColorClass = 'bg-blue-50 border-l-blue-500';
                                  if (isBreak) bgColorClass = 'bg-amber-50 border-l-amber-500';
                                  else if (isCustom) bgColorClass = 'bg-orange-50 border-l-orange-500';
                                  else if (isLab) bgColorClass = 'bg-purple-50 border-l-purple-500';
                                  else if (isLanguage) bgColorClass = 'bg-green-50 border-l-green-500';
                                  else if (isElective) bgColorClass = 'bg-indigo-50 border-l-indigo-500';
                                  
                                  return (
                                    <div 
                                      key={entry._id || entryIndex} 
                                      className={`p-3 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md border ${bgColorClass} ${isOverrideActive ? 'border-l-orange-500' : ''}`}
                                      onClick={() => handleEntryClick(entry, dateISO)}
                                    >
                                      {isOverrideActive && (
                                        <div className="mb-2 text-xs font-medium text-orange-600 flex items-center gap-1">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                          <span>Override Entry</span>
                                        </div>
                                      )}
                                      
                                      {!isBreak && !isCustom && (
                                        <div className="flex items-center gap-1 mb-1 flex-wrap">
                                          {entry.subjectCode && (
                                            <span className="text-[10px] bg-gray-200 px-1.5 py-0.5 rounded font-mono">
                                              {entry.subjectCode}
                                            </span>
                                          )}
                                          {isElective && entry.electiveGroupLabel && (
                                            <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold">
                                              {entry.electiveGroupLabel}
                                            </span>
                                          )}
                                          {isLanguage && (
                                            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold">
                                              Language
                                            </span>
                                          )}
                                          {isLab && (
                                            <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold">
                                              Lab
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      
                                      <div className="font-semibold text-gray-900 text-sm leading-tight">
                                        {isBreak ? (entry.label || 'BREAK') :
                                         isCustom ? (entry.label || 'CUSTOM') :
                                         isElective ? (entry.electiveGroupDisplay || getGroupDisplayName(entry.electiveGroupId)) :
                                         entry.subjectName || entry.label || entry.type}
                                      </div>
                                      
                                      <div className="mt-2 text-xs text-gray-600">
                                        {entry.facultyName && !isElective && !isBreak && !isCustom && !isLab && (
                                          <div className="truncate">👨‍🏫 {entry.facultyName}</div>
                                        )}
                                        {isLab && entry.batchName && (
                                          <div className="text-purple-700 font-medium">🧪 {entry.batchName}</div>
                                        )}
                                        {isLab && entry.facultyName && (
                                          <div className="truncate text-gray-600">👨‍🏫 {entry.facultyName}</div>
                                        )}
                                        {isElective && (
                                          <div className="text-indigo-600">
                                            {entry.electiveSelections?.length || 0} Option(s) Available
                                          </div>
                                        )}
                                        {entry.room && (
                                          <div className="text-gray-500">🏠 {entry.room}</div>
                                        )}
                                      </div>
                                      
                                      <div className="mt-2 text-[10px] text-gray-400 text-right">
                                        Click for details →
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-8">
                                <div className="text-gray-400 text-sm">No Class</div>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {renderEntryModal()}

      {/* Legend */}
      <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
            <span className="text-gray-700">Regular Class</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-indigo-500 rounded-sm"></div>
            <span className="text-gray-700">Elective</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
            <span className="text-gray-700">Language</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-purple-500 rounded-sm"></div>
            <span className="text-gray-700">Laboratory / Practical</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded-sm"></div>
            <span className="text-gray-700">Break</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-sm"></div>
            <span className="text-gray-700">Custom Session</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
            <span className="text-gray-700">Exam / Holiday</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-sm"></div>
            <span className="text-gray-700">Override Active</span>
          </div>
        </div>
      </div>
    </div>
  );
}